import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AutoComplete, Avatar, Button, Checkbox, DatePicker, Input, InputNumber, Popover, Select, Spin, Tag, Tooltip, message } from 'antd'
import { DeleteOutlined, MessageOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { leadsApi, type CrmDictionaries, type Lead, type LeadChecklistItem, type LeadMethod } from '../../services/leadsApi'
import { temperatureColor, temperatureLabel } from '../../constants/leadTemperature'
import { profileLabel } from '../../services/visibilityProfilesApi'
import './LeadCard.css'

const { TextArea } = Input

type Kind = 'text' | 'textarea' | 'select' | 'date' | 'link' | 'digits' | 'operator' | 'person' | 'source' | 'geo'

interface CardField {
    key: keyof Lead
    label: string
    kind?: Kind
    options?: string[]
    // Метки шаблона: обязательно для создания, для онбординга, ключ дублей,
    // используется в дашбордах, не правится.
    create?: boolean
    onb?: boolean
    dup?: boolean
    dash?: boolean
    locked?: boolean
    // Контакт: у роли со скрытыми контактами показывается как «скрыто».
    contact?: boolean
    wide?: boolean
    hint?: string
    placeholder?: string
}

interface CardGroup {
    id: string
    title: string
    fields: CardField[]
    collapsed?: boolean
}

const NO_CHOICE = 'Не выбрано'

const MAIN: CardField[] = [
    { key: 'title', label: 'Название лида', create: true, hint: 'TG username → название проекта на онбординге → почта → телефон. Должно быть уникальным.' },
    { key: 'source', label: 'Источник', kind: 'source', create: true, dash: true },
    { key: 'source_details', label: 'Уточнение источника', create: true, hint: 'Место, проект реферала или TG менеджера, от которого пришёл лид.' },
    { key: 'geo', label: 'ГЕО', kind: 'geo', create: true, dash: true },
    { key: 'discovery_source', label: 'Откуда агент узнал о нас', kind: 'select', dash: true },
    { key: 'admin_panel', label: 'Админка WiseWay', hint: 'Почта из карточки агента в продукте.' },
    { key: 'crm_methods', label: 'Методы и условия', onb: true, dash: true },
    { key: 'prepay', label: 'Препей', onb: true },
    { key: 'additional_info', label: 'Доп. информация', kind: 'textarea', wide: true, hint: 'Ответы на вопросы, банки агента, количество аккаунтов.' },
]

const CONTACTS: CardField[] = [
    { key: 'first_name', label: 'Имя', onb: true, dup: true },
    { key: 'last_name', label: 'Фамилия', onb: true, dup: true },
    { key: 'birth_date', label: 'Дата рождения', kind: 'date', onb: true, dup: true, hint: 'Дубль, если совпали имя, фамилия и дата рождения.' },
    { key: 'gender', label: 'Пол', kind: 'select' },
    // Телеграм заперт после заведения: по нему привязан чат и сверяется
    // карточка CRM Wiseway.
    { key: 'telegram_nickname', label: 'TG Nickname', create: true, onb: true, dup: true, locked: true, contact: true },
    // TG ID скрывается своим переключателем, а не вместе с контактами.
    { key: 'telegram_id', label: 'TG ID', kind: 'digits', dup: true, hint: 'Только цифры.' },
    { key: 'email', label: 'Email', onb: true, dup: true },
    { key: 'phone', label: 'Телефон', onb: true, dup: true, contact: true },
    { key: 'extra_contacts', label: 'Доп. контакты', kind: 'textarea', wide: true, contact: true },
]

const ONBOARDING: CardField[] = [
    { key: 'document_1', label: 'Документ №1', kind: 'select', onb: true },
    { key: 'document_2', label: 'Документ №2', kind: 'select', onb: true },
    { key: 'kyc_id', label: 'ID KYC', hint: 'ID из Sumsub.' },
    { key: 'kyc_link', label: 'Ссылка на KYC', kind: 'link' },
    { key: 'chat_link', label: 'Ссылка на чат', kind: 'link', onb: true, contact: true },
    { key: 'agent_agreement_link', label: 'Agent Agreement', kind: 'link' },
    { key: 'jira_task_link', label: 'Задача в Jira', kind: 'link' },
    { key: 'offboarding_am', label: 'Ответственный АМ за отключение', kind: 'person' },
]

// Устаревшие поля: в новых карточках не используются, но данные в них есть -
// спрятаны в отдельную шторку и показываются по переключателю.
const LEGACY: CardField[] = [
    { key: 'onboarding_manager', label: 'Менеджер онбординга', kind: 'person' },
    { key: 'onboarding_am', label: 'Ответственный АМ за онбординг', kind: 'person' },
    { key: 'onboarded_at', label: 'Дата онборда', kind: 'date' },
    { key: 'lead_type', label: 'Тип', kind: 'select' },
    { key: 'agent_type', label: 'Вид агента', kind: 'select' },
    { key: 'drop_agreement_link', label: 'Drop Agreement', kind: 'link' },
    { key: 'company', label: 'Компания' },
    { key: 'position', label: 'Должность' },
    { key: 'link', label: 'Ссылка', kind: 'link' },
    { key: 'key_value', label: 'Ключевое значение' },
    { key: 'document_type', label: 'Тип документа' },
    { key: 'legacy_conditions', label: 'Условия (Legacy)', kind: 'textarea', wide: true },
    // Прежние текстовые чек-листы: были только у нас, теперь чек-листы - списки
    // с отметками из CRM Wiseway. Старые записи оставлены, чтобы не пропали.
    { key: 'am_onboarding_checklist', label: 'Чек-лист АМ (старый текст)', kind: 'textarea', wide: true },
    { key: 'finalization_checklist', label: 'Чек-лист финализации (старый текст)', kind: 'textarea', wide: true },
    { key: 'offboarding_checklist', label: 'Чек-лист отключения (старый текст)', kind: 'textarea', wide: true },
]

const STATUS_FIELDS: CardField[] = [
    { key: 'assigned_to_id', label: 'Ведёт', kind: 'operator' },
    { key: 'manager', label: 'Менеджер', kind: 'person', hint: 'Вручную или автоматически по настройкам автораспределения источника.' },
    { key: 'refusal_reason', label: 'Причина отказа', kind: 'textarea', wide: true },
    { key: 'contact_email', label: 'Контактный e-mail', locked: true },
    { key: 'created_by_name', label: 'Кем создан', locked: true },
]

const UTM: CardField[] = [
    { key: 'utm_source', label: 'utm_source', locked: true },
    { key: 'utm_medium', label: 'utm_medium', locked: true },
    { key: 'utm_campaign', label: 'utm_campaign', locked: true },
    { key: 'utm_content', label: 'utm_content', locked: true },
    { key: 'utm_term', label: 'utm_term', locked: true },
    { key: 'landing_page', label: 'Страница заявки', kind: 'link', locked: true },
]

const GROUPS: CardGroup[] = [
    { id: 'main', title: 'Основная информация', fields: MAIN },
    { id: 'contacts', title: 'Контактная информация', fields: CONTACTS },
    { id: 'onb', title: 'Детали онбординга и ссылки', fields: ONBOARDING },
    { id: 'status', title: 'Статус и ответственный', fields: STATUS_FIELDS },
    { id: 'utm', title: 'Маркетинговые метки (UTM)', fields: UTM, collapsed: true },
]

// Поля, которые появляются на своём статусе: отказ по цене, по препею, отключение.
function conditionalFields(status: string | null | undefined): CardField[] {
    if (status === 'rej_price') return [{ key: 'desired_terms', label: 'Желаемые условия', placeholder: '4.8/2.5', hint: 'Условия, которые просит агент: приём/вывод.' }]
    if (status === 'rej_prepay') return [{ key: 'desired_amount', label: 'Желаемая сумма', kind: 'select' }]
    if (status?.startsWith('disconnect')) {
        return [
            { key: 'disconnection_reason', label: 'Причина отключения', kind: 'select' },
            { key: 'disconnection_comment', label: 'Комментарий к отключению', kind: 'textarea', wide: true },
        ]
    }
    return []
}

const LEFTOVER_FIELDS: CardField[] = [
    { key: 'desired_terms', label: 'Желаемые условия' },
    { key: 'desired_amount', label: 'Желаемая сумма', kind: 'select' },
    { key: 'disconnection_reason', label: 'Причина отключения', kind: 'select' },
    { key: 'disconnection_comment', label: 'Комментарий к отключению', kind: 'textarea', wide: true },
]
const CONDITIONAL_KEYS: (keyof Lead)[] = LEFTOVER_FIELDS.map((field) => field.key)

// Статусы онбординга - только для цвета статуса в шапке.
const ONBOARDING_STATUS_RANGE = ['chat_approval', 'to_onboarding', 'in_progress_am', 'kyc_pending', 'kyc_issues', 'kyc_revised', 'waiting_prepay', 'prepay_paid', 'connected_agent']
const WORK_STATUSES = ['new', 'new_assigned', 'first_msg', 'collecting_info', 'offer_sent', 'waiting_decision', 'agent_agreed', 'reactivation']
// Поля, без которых сервер не даёт поставить часть статусов. Сам список
// статусов и проверка - на сервере (app/services/status_requirements.py), здесь
// только ссылки из текста отказа на поля карточки.
const REQUIRED_FIELD_BY_LABEL: Record<string, keyof Lead> = {
    'TG ID': 'telegram_id',
    'Телефон': 'phone',
    'Имя': 'first_name',
    'Фамилия': 'last_name',
    'Дата рождения': 'birth_date',
}

// Подписи из ответа сервера (onboarding_missing) -> поле карточки.
const MISSING_TO_FIELD: Record<string, keyof Lead> = {
    'Имя': 'first_name',
    'Фамилия': 'last_name',
    'TG Nickname': 'telegram_nickname',
    'Email': 'email',
    'Телефон': 'phone',
    'Дата рождения': 'birth_date',
    'Документ №1': 'document_1',
    'Документ №2': 'document_2',
    'Ссылка на чат': 'chat_link',
    'Метод': 'crm_methods',
    'Прием, %': 'crm_methods',
    'Вывод, %': 'crm_methods',
    'Препей': 'prepay',
}
const ONBOARDING_TOTAL = new Set(Object.values(MISSING_TO_FIELD)).size

const CHECKLIST_ORDER = ['am_onboarding', 'prepay_finalization', 'offboarding']

const OPTS_KEY = 'orchestrator.leadCard.view'
const CLOSED_KEY = 'orchestrator.leadCard.closed'

interface ViewOptions {
    empty: boolean
    missing: boolean
    legacy: boolean
}

function loadJson<T>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key)
        return raw ? { ...fallback, ...JSON.parse(raw) } : fallback
    } catch {
        return fallback
    }
}

function saveJson(key: string, value: unknown) {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Без памяти браузера карточка работает так же, просто не помнит вид.
    }
}

function extractError(error: unknown): string {
    if (typeof error === 'object' && error && 'response' in error) {
        const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
        if (typeof detail === 'string') return detail
    }
    return error instanceof Error ? error.message : 'Не удалось выполнить действие'
}

function errorStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } })?.response?.status
}

function isFilledValue(value: unknown): boolean {
    if (value === null || value === undefined) return false
    const text = String(value).trim()
    return Boolean(text) && text !== NO_CHOICE
}

export interface OperatorOption {
    id: number
    username: string
    full_name?: string | null
    department?: string | null
}

interface LeadCardProps {
    lead: Lead
    geos: string[]
    crmDict: CrmDictionaries | null
    operators: OperatorOption[]
    canEdit: boolean
    canChangeStatus: boolean
    onLeadChange: (lead: Lead) => void
    onReload: () => Promise<void> | void
    onOpenChat?: () => void
}

export default function LeadCard({ lead, geos, crmDict, operators, canEdit, canChangeStatus, onLeadChange, onReload, onOpenChat }: LeadCardProps) {
    const [opts, setOpts] = useState<ViewOptions>(() => loadJson(OPTS_KEY, { empty: true, missing: false, legacy: false }))
    const [closed, setClosed] = useState<Record<string, boolean>>(() => {
        const saved = loadJson<Record<string, boolean>>(CLOSED_KEY, {})
        GROUPS.forEach((group) => {
            if (group.collapsed && !(group.id in saved)) saved[group.id] = true
        })
        return saved
    })
    const [editing, setEditing] = useState<keyof Lead | null>(null)
    const [draft, setDraft] = useState<unknown>(null)
    const [savingKey, setSavingKey] = useState<string | null>(null)
    const [gate, setGate] = useState<{ title: string; items: { label: string; key?: keyof Lead }[]; text?: string } | null>(null)
    const [statusGroups, setStatusGroups] = useState<{ group: string; reasons: { code: string; label: string }[] }[]>([])
    const [statusSaving, setStatusSaving] = useState(false)
    const [flashKey, setFlashKey] = useState<string | null>(null)
    const rootRef = useRef<HTMLDivElement>(null)

    useEffect(() => saveJson(OPTS_KEY, opts), [opts])
    useEffect(() => saveJson(CLOSED_KEY, closed), [closed])

    // Статусы для выбора в шапке - только доступные роли, сервер отдаёт их
    // уже отфильтрованными.
    useEffect(() => {
        if (!canChangeStatus) {
            setStatusGroups([])
            return
        }
        let alive = true
        leadsApi.getCrmStatusOptions(lead.id)
            .then(({ data }) => { if (alive) setStatusGroups(data.groups) })
            .catch(() => { if (alive) setStatusGroups([]) })
        return () => { alive = false }
    }, [lead.id, canChangeStatus])

    useEffect(() => {
        setGate(null)
        setEditing(null)
    }, [lead.id])

    const masked = Boolean(lead.contacts_hidden)
    const isHidden = (field: CardField) => (masked && Boolean(field.contact)) || (field.key === 'telegram_id' && Boolean(lead.telegram_id_hidden))
    const missingKeys = useMemo(() => {
        const keys = new Set<keyof Lead>()
        ;(lead.onboarding_missing || []).forEach((label) => {
            const key = MISSING_TO_FIELD[label]
            if (key) keys.add(key)
        })
        return keys
    }, [lead.onboarding_missing])
    const readyDone = ONBOARDING_TOTAL - missingKeys.size

    const fieldOptions = useCallback((field: CardField): string[] => {
        if (field.options) return field.options
        return crmDict?.options?.[String(field.key)] || []
    }, [crmDict])

    const fieldFilled = useCallback((field: CardField): boolean => {
        if (field.key === 'crm_methods') return !missingKeys.has('crm_methods') && Boolean(lead.crm_methods?.length)
        return isFilledValue(lead[field.key])
    }, [lead, missingKeys])

    const isMissing = (field: CardField) => Boolean(field.onb) && missingKeys.has(field.key)

    // ---- сохранение ----

    const saveField = async (field: CardField, value: unknown) => {
        const current = lead[field.key]
        let next: unknown = typeof value === 'string' ? value.trim() : value
        if (next === '' || next === undefined) next = null
        if (field.kind === 'digits' && typeof next === 'string') next = next.replace(/\D/g, '') || null
        if (field.kind === 'date' && next && dayjs.isDayjs(next)) next = next.format('YYYY-MM-DD')
        setEditing(null)
        if ((next ?? null) === (current ?? null) || String(next ?? '') === String(current ?? '')) return
        if (field.create && next === null) {
            message.warning(`«${field.label}» обязательно для карточки`)
            return
        }
        setSavingKey(String(field.key))
        try {
            const { data } = await leadsApi.update(lead.id, { [field.key]: next } as Partial<Lead>)
            onLeadChange(data)
            if (data.crm_warning) message.warning(data.crm_warning, 6)
            else message.success('Сохранено')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSavingKey(null)
        }
    }

    const startEdit = (field: CardField) => {
        if (!canEdit || field.locked || isHidden(field) || savingKey) return
        const value = lead[field.key]
        setDraft(field.kind === 'date' ? (value ? dayjs(String(value)) : null) : value ?? '')
        setEditing(field.key)
    }

    // ---- статус ----

    const statusLabel = lead.crm_status_label || (lead.crm_status ? lead.crm_status : 'Статус не прочитан')
    const statusClass = lead.crm_fraud ? ' is-fraud' : ONBOARDING_STATUS_RANGE.includes(lead.crm_status || '')
        ? ' is-onb'
        : WORK_STATUSES.includes(lead.crm_status || '') ? ' is-work' : ''

    const changeStatus = async (target: string) => {
        const label = statusGroups.flatMap((group) => group.reasons).find((item) => item.code === target)?.label || target

        setGate(null)
        setStatusSaving(true)
        try {
            const { data } = await leadsApi.setCrmStatus(lead.id, target)
            if (data.warning) message.warning(data.warning, 6)
            else message.success(`Статус: ${label}`)
            await onReload()
        } catch (error) {
            if (errorStatus(error) === 400) {
                // Отказ сервера: «... Заполните: TG ID, Телефон» - поля делаем ссылками.
                const text = extractError(error)
                const listed = text.includes('Заполните:') ? text.split('Заполните:')[1].split(',').map((item) => item.trim()).filter(Boolean) : []
                setGate(listed.length
                    ? { title: `Нельзя перевести в «${label}»`, items: listed.map((item) => ({ label: item, key: REQUIRED_FIELD_BY_LABEL[item] })) }
                    : { title: `Нельзя перевести в «${label}»`, items: [], text })
            }
            else message.error(extractError(error))
        } finally {
            setStatusSaving(false)
        }
    }

    // ---- навигация ----

    const groupOf = (key: keyof Lead | string) => {
        if (CONDITIONAL_KEYS.includes(key as keyof Lead)) return 'status'
        return GROUPS.find((group) => group.fields.some((field) => field.key === key))?.id
            || (LEGACY.some((field) => field.key === key) ? 'onb' : 'main')
    }

    // Прокрутка к полю с учётом закреплённой шапки: иначе поле оказывалось
    // прямо под ней.
    const scrollToElement = (element: Element | null | undefined) => {
        if (!element) return
        const scroller = element.closest('.ant-drawer-body') as HTMLElement | null
        const head = rootRef.current?.querySelector('.lc-head') as HTMLElement | null
        if (!scroller) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return
        }
        const offset = element.getBoundingClientRect().top - scroller.getBoundingClientRect().top
        const headHeight = head?.getBoundingClientRect().height || 0
        scroller.scrollTo({ top: scroller.scrollTop + offset - headHeight - 12, behavior: 'smooth' })
    }

    const flash = (key: string) => {
        const groupId = groupOf(key)
        setClosed((current) => ({ ...current, [groupId]: false }))
        window.setTimeout(() => {
            scrollToElement(rootRef.current?.querySelector(`[data-row="${key}"]`) || rootRef.current?.querySelector(`[data-group="${groupId}"]`))
            setFlashKey(null)
            window.setTimeout(() => setFlashKey(key), 20)
        }, 30)
    }

    const jumpTo = (group: CardGroup) => {
        const firstMissing = group.fields.find((field) => isMissing(field))
        if (firstMissing) {
            flash(String(firstMissing.key))
            return
        }
        setClosed((current) => ({ ...current, [group.id]: false }))
        window.setTimeout(() => scrollToElement(rootRef.current?.querySelector(`[data-group="${group.id}"]`)), 30)
    }

    const toggleGroup = (id: string) => setClosed((current) => ({ ...current, [id]: !current[id] }))
    const allClosed = GROUPS.every((group) => closed[group.id])
    const setAll = (value: boolean) => setClosed(Object.fromEntries(GROUPS.map((group) => [group.id, value])))
    const setOpt = (key: keyof ViewOptions, value: boolean) => {
        setOpts((current) => ({ ...current, [key]: value }))
        if (key === 'missing' && value) setAll(false)
    }

    // ---- отрисовка значения ----

    const renderMarks = (field: CardField) => (
        <>
            {field.create && <Tooltip title="Обязательно для создания карточки"><span className="lc-mark is-create">создание</span></Tooltip>}
            {field.onb && <Tooltip title="Обязательно для статусов онбординга"><span className="lc-mark is-onb">онбординг</span></Tooltip>}
            {field.dup && <Tooltip title="По полю сводятся дубли"><span className="lc-mark is-dup">дубль</span></Tooltip>}
            {field.dash && <Tooltip title="Используется в плане и дашбордах"><span className="lc-mark is-dash">дашборд</span></Tooltip>}
            {field.locked && <Tooltip title="Не редактируется: связь с CRM / Telegram"><span className="lc-mark is-lock">🔒</span></Tooltip>}
        </>
    )

    const displayValue = (field: CardField): ReactNode => {
        const raw = lead[field.key]
        if (field.key === 'assigned_to_id') return lead.assigned_to_name || (raw ? `#${raw}` : null)
        if (!isFilledValue(raw)) return null
        const value = String(raw)
        if (field.kind === 'date') return dayjs(value).format('DD.MM.YYYY')
        if (field.kind === 'link' || /^https?:\/\//.test(value)) {
            return /^https?:\/\//.test(value)
                ? <a href={value} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>{value.replace(/^https?:\/\//, '')}</a>
                : value
        }
        return value
    }

    const renderEditor = (field: CardField) => {
        const commit = (value: unknown) => saveField(field, value)
        const cancel = () => setEditing(null)
        const keys = {
            onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Escape') cancel()
            },
        }
        if (field.kind === 'textarea') {
            return (
                <TextArea
                    autoFocus
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    value={String(draft ?? '')}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => commit(draft)}
                    {...keys}
                />
            )
        }
        if (field.kind === 'date') {
            return (
                <DatePicker
                    autoFocus
                    defaultOpen
                    format="DD.MM.YYYY"
                    value={draft as dayjs.Dayjs | null}
                    onChange={(value) => commit(value)}
                    onOpenChange={(open) => { if (!open) window.setTimeout(cancel, 150) }}
                />
            )
        }
        const selectProps = {
            autoFocus: true,
            defaultOpen: true,
            showSearch: true,
            allowClear: !field.create,
            optionFilterProp: 'label',
            size: 'middle' as const,
            onBlur: cancel,
            ...keys,
        }
        if (field.kind === 'operator') {
            return (
                <Select
                    {...selectProps}
                    value={(draft as number | null) ?? undefined}
                    placeholder="Выберите сотрудника"
                    options={operators.map((operator) => ({
                        value: operator.id,
                        label: `${operator.full_name || operator.username}${operator.department ? ` · ${operator.department}` : ''}`,
                    }))}
                    onChange={(value) => commit(value ?? null)}
                />
            )
        }
        if (field.kind === 'select' || field.kind === 'source' || field.kind === 'geo') {
            const base = field.kind === 'source' ? crmDict?.sources || [] : field.kind === 'geo' ? geos : fieldOptions(field)
            const current = String(lead[field.key] ?? '')
            const list = [...base, ...(current && !base.includes(current) ? [current] : [])]
            return (
                <Select
                    {...selectProps}
                    value={(draft as string) || undefined}
                    placeholder="Выберите значение"
                    options={list.map((item) => ({ value: item, label: item }))}
                    onChange={(value) => commit(value ?? null)}
                />
            )
        }
        if (field.kind === 'person') {
            return (
                <AutoComplete
                    autoFocus
                    value={String(draft ?? '')}
                    onChange={(value) => setDraft(value)}
                    onSelect={(value) => commit(value)}
                    onBlur={() => commit(draft)}
                    filterOption={(input, option) => String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                    options={operators.map((operator) => ({ value: operator.full_name || operator.username }))}
                    {...keys}
                />
            )
        }
        return (
            <Input
                autoFocus
                value={String(draft ?? '')}
                placeholder={field.placeholder}
                inputMode={field.kind === 'digits' ? 'numeric' : undefined}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => commit(draft)}
                onPressEnter={(event) => (event.target as HTMLInputElement).blur()}
                {...keys}
            />
        )
    }

    const renderRow = (field: CardField, force = false) => {
        if (field.key === 'crm_methods') return renderMethods(field)
        const filled = fieldFilled(field)
        const missing = isMissing(field)
        if (!force) {
            if (!opts.empty && !filled && !field.create && !field.onb) return null
            if (opts.missing && !missing) return null
        }
        const hidden = isHidden(field)
        const locked = !canEdit || field.locked || hidden
        const value = hidden ? null : displayValue(field)
        const isEditing = editing === field.key
        const classes = ['lc-row']
        if (field.wide) classes.push('is-wide')
        if (missing) classes.push('is-missing')
        if (flashKey === field.key) classes.push('is-flash')
        let body: ReactNode
        if (isEditing) {
            body = <div className="lc-val is-editing">{renderEditor(field)}</div>
        } else if (hidden) {
            body = <span className="lc-val is-masked is-locked">скрыто</span>
        } else {
            const valueClasses = ['lc-val']
            if (value === null) valueClasses.push('is-empty')
            if (locked) valueClasses.push('is-locked')
            body = (
                <span
                    className={valueClasses.join(' ')}
                    role={locked ? undefined : 'button'}
                    tabIndex={locked ? -1 : 0}
                    onClick={() => startEdit(field)}
                    onKeyDown={(event) => { if (event.key === 'Enter') startEdit(field) }}
                >
                    {savingKey === field.key ? <Spin size="small" /> : (value ?? 'не задано')}
                </span>
            )
        }
        return (
            <div className={classes.join(' ')} data-row={String(field.key)} key={String(field.key)}>
                <div className="lc-key">
                    <span className="lc-key-label" title={field.hint ? `${field.label} — ${field.hint}` : field.label}>{field.label}</span>
                    {renderMarks(field)}
                </div>
                {body}
                {field.hint && !filled && <div className="lc-hint">{field.hint}</div>}
            </div>
        )
    }

    // ---- методы и условия ----

    const [methods, setMethods] = useState<LeadMethod[]>([])
    const methodsDirty = useRef(false)
    const methodsSource = JSON.stringify(lead.crm_methods || [])
    useEffect(() => {
        if (methodsDirty.current) return
        const list = (lead.crm_methods || []).map((item) => ({ ...item }))
        setMethods(list.length ? list : [{ method: '', conditions_deposit: null, conditions_withdrawal: null }])
    }, [methodsSource, lead.id])

    const saveMethods = async (rows: LeadMethod[]) => {
        const clean = rows
            .filter((row) => row.method || row.conditions_deposit !== null || row.conditions_withdrawal !== null)
            .map((row) => ({ ...row, method: row.method || NO_CHOICE }))
        if (clean.some((row) => row.conditions_deposit === 0 && row.conditions_withdrawal === 0)) {
            message.warning('Нельзя ставить 0 одновременно в «Приём» и «Вывод» — это правило CRM Wiseway')
            return
        }
        if (JSON.stringify(clean) === JSON.stringify(lead.crm_methods || [])) {
            methodsDirty.current = false
            return
        }
        setSavingKey('crm_methods')
        try {
            const { data } = await leadsApi.update(lead.id, { crm_methods: clean })
            methodsDirty.current = false
            onLeadChange(data)
            if (data.crm_warning) message.warning(data.crm_warning, 6)
            else message.success('Методы сохранены')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSavingKey(null)
        }
    }

    const updateMethod = (index: number, patch: Partial<LeadMethod>, save = false) => {
        methodsDirty.current = true
        const next = methods.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row)
        setMethods(next)
        if (save) saveMethods(next)
    }

    function renderMethods(field: CardField) {
        const missing = isMissing(field)
        if (opts.missing && !missing) return null
        const allowed = crmDict?.geo_methods?.[lead.geo || ''] || crmDict?.method_fallback || []
        const editable = canEdit
        return (
            <div className={`lc-box${flashKey === 'crm_methods' ? ' is-flash' : ''}`} data-row="crm_methods" key="crm_methods">
                <div className="lc-box-head">
                    <span style={missing ? { color: 'var(--lc-onb)' } : undefined}>Методы и условия</span>
                    {renderMarks(field)}
                    <span className="lc-spacer" />
                    {savingKey === 'crm_methods' && <Spin size="small" />}
                    {editable && (
                        <button
                            className="lc-btn lc-plain"
                            type="button"
                            onClick={() => { methodsDirty.current = true; setMethods([...methods, { method: '', conditions_deposit: null, conditions_withdrawal: null }]) }}
                        >
                            <PlusOutlined /> Метод
                        </button>
                    )}
                </div>
                <table className="lc-methods">
                    <thead>
                        <tr>
                            <th>Метод</th>
                            <th className="is-num">Приём, %</th>
                            <th className="is-num">Вывод, %</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {methods.map((row, index) => (
                            <tr key={index}>
                                <td>
                                    {editable ? (
                                        <Select
                                            size="small"
                                            allowClear
                                            showSearch
                                            placeholder={NO_CHOICE}
                                            value={row.method && row.method !== NO_CHOICE ? row.method : undefined}
                                            options={[...allowed, ...(row.method && row.method !== NO_CHOICE && !allowed.includes(row.method) ? [row.method] : [])]
                                                .map((item) => ({ value: item, label: item }))}
                                            onChange={(value) => updateMethod(index, { method: value || '' }, true)}
                                        />
                                    ) : (row.method || NO_CHOICE)}
                                </td>
                                <td className="is-num">
                                    {editable ? (
                                        <InputNumber
                                            size="small"
                                            min={0}
                                            step={0.1}
                                            placeholder="—"
                                            value={row.conditions_deposit}
                                            onChange={(value) => updateMethod(index, { conditions_deposit: value ?? null })}
                                            onBlur={() => saveMethods(methods)}
                                        />
                                    ) : (row.conditions_deposit ?? '—')}
                                </td>
                                <td className="is-num">
                                    {editable ? (
                                        <InputNumber
                                            size="small"
                                            min={0}
                                            step={0.1}
                                            placeholder="—"
                                            value={row.conditions_withdrawal}
                                            onChange={(value) => updateMethod(index, { conditions_withdrawal: value ?? null })}
                                            onBlur={() => saveMethods(methods)}
                                        />
                                    ) : (row.conditions_withdrawal ?? '—')}
                                </td>
                                <td className="is-del">
                                    {editable && (
                                        <Button
                                            size="small"
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            aria-label="Удалить метод"
                                            onClick={() => {
                                                methodsDirty.current = true
                                                const next = methods.filter((_, rowIndex) => rowIndex !== index)
                                                setMethods(next.length ? next : [{ method: '', conditions_deposit: null, conditions_withdrawal: null }])
                                                saveMethods(next)
                                            }}
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="lc-hint" style={{ padding: '6px 12px 8px' }}>Список методов зависит от ГЕО. По полю считается план.</div>
            </div>
        )
    }

    // ---- чек-листы ----

    const [checkSaving, setCheckSaving] = useState<string | null>(null)

    const toggleCheck = async (key: string, item: LeadChecklistItem, checked: boolean) => {
        setCheckSaving(`${key}:${item.id}`)
        try {
            const { data } = await leadsApi.toggleChecklist(lead.id, key, item.id, checked)
            onLeadChange(data)
        } catch (error) {
            message.error(extractError(error), errorStatus(error) === 409 ? 6 : 3)
            if (errorStatus(error) === 409) await onReload()
        } finally {
            setCheckSaving(null)
        }
    }

    const renderChecklists = () => {
        if (opts.missing) return null
        return CHECKLIST_ORDER.filter((key) => lead.checklist_info?.[key]).map((key) => {
            const info = lead.checklist_info![key]
            const items = lead.checklists?.[key] || []
            const done = items.filter((item) => item.is_checked).length
            return (
                <div className="lc-box" key={key}>
                    <div className="lc-box-head">
                        {info.title}
                        <span className="lc-spacer" />
                        <span className="lc-plain">{done} из {items.length}</span>
                    </div>
                    <div className="lc-check-bar"><i style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} /></div>
                    <ul className="lc-checks">
                        {items.map((item) => (
                            <li key={item.id}>
                                <Checkbox
                                    checked={item.is_checked}
                                    disabled={Boolean(checkSaving)}
                                    onChange={(event) => toggleCheck(key, item, event.target.checked)}
                                />
                                <div>
                                    <div>{item.label}</div>
                                    {item.last_user && (
                                        <div className="lc-who">
                                            {item.last_user}{item.updated_at ? ` · ${dayjs(item.updated_at).format('DD.MM HH:mm')}` : ''}
                                        </div>
                                    )}
                                </div>
                                {checkSaving === `${key}:${item.id}` && <Spin size="small" />}
                            </li>
                        ))}
                    </ul>
                    <div className="lc-hint" style={{ padding: '0 12px 10px' }}>
                        {info.description}
                    </div>
                </div>
            )
        })
    }

    // ---- группы ----

    const renderGroup = (group: CardGroup) => {
        const onbFields = group.fields.filter((field) => field.onb)
        const missing = onbFields.filter((field) => isMissing(field)).length
        const done = group.fields.filter((field) => fieldFilled(field)).length
        let rows: ReactNode[] = group.fields.map((field) => renderRow(field)).filter(Boolean)
        if (group.id === 'status') {
            const extra = conditionalFields(lead.crm_status)
            // Значение, заполненное на прошлом статусе, не прячем.
            const shown = new Set(extra.map((field) => field.key))
            const leftovers = LEFTOVER_FIELDS.filter((field) => !shown.has(field.key) && isFilledValue(lead[field.key]))
            if (extra.length && !opts.missing) {
                rows = [
                    <div className="lc-cond" key="cond">
                        <div className="lc-cond-tag">Появилось для статуса «{lead.crm_status_label || lead.crm_status}»</div>
                        {extra.map((field) => renderRow(field, true))}
                    </div>,
                    ...rows,
                ]
            }
            rows = [...rows, ...leftovers.map((field) => renderRow(field))]
        }
        if (group.id === 'onb') {
            rows.push(<div key="checklists">{renderChecklists()}</div>)
            if (opts.legacy && !opts.missing) {
                rows.push(
                    <div className="lc-box" key="legacy">
                        <div className="lc-box-head">
                            Устаревшие поля
                            <span className="lc-spacer" />
                            <span className="lc-plain">не используются в новых карточках</span>
                        </div>
                        {LEGACY.map((field) => renderRow(field))}
                    </div>
                )
            }
        }
        const isClosed = Boolean(closed[group.id])
        return (
            <section className={`lc-grp${isClosed ? ' is-closed' : ''}`} data-group={group.id} key={group.id}>
                <button className="lc-grp-head" type="button" aria-expanded={!isClosed} onClick={() => toggleGroup(group.id)}>
                    <span className="lc-grp-title">{group.title}</span>
                    <span className="lc-grp-count">{done} / {group.fields.length}</span>
                    {onbFields.length > 0 && (missing
                        ? <span className="lc-badge"><span className="lc-long">для онбординга не хватает {missing}</span><span className="lc-short">−{missing}</span></span>
                        : <span className="lc-badge is-ok"><span className="lc-long">готово к онбордингу</span><span className="lc-short">✓</span></span>)}
                    <span className="lc-chev" aria-hidden="true">▾</span>
                </button>
                <div className="lc-grp-body">
                    {rows.length ? rows : <div className="lc-hint" style={{ padding: '6px 10px' }}>В этой группе всё заполнено.</div>}
                </div>
            </section>
        )
    }

    const viewOptions = (
        <div style={{ display: 'grid', gap: 6 }}>
            <Checkbox checked={opts.empty} onChange={(event) => setOpt('empty', event.target.checked)}>Пустые поля</Checkbox>
            <Checkbox checked={opts.missing} onChange={(event) => setOpt('missing', event.target.checked)}>Только чего не хватает</Checkbox>
            <Checkbox checked={opts.legacy} onChange={(event) => setOpt('legacy', event.target.checked)}>Устаревшие поля</Checkbox>
            <Checkbox checked={allClosed} onChange={(event) => setAll(event.target.checked)}>Свернуть все группы</Checkbox>
        </div>
    )

    const responsible = lead.visibility_profile_name
        ? profileLabel(lead.visibility_profile_name, lead.visibility_profile_email)
        : (lead.assigned_to_name || 'не назначен')
    const account = lead.account_username ? `@${lead.account_username}` : (lead.account_phone || 'не выбран')
    const initials = (lead.title || '?').replace(/^@/, '').slice(0, 2).toUpperCase()

    return (
        <div className="lc" ref={rootRef}>
            <header className={`lc-head${lead.crm_fraud ? ' is-fraud' : ''}`}>
                <div className="lc-top">
                    <Avatar className="lc-ava" size={44} src={lead.telegram_avatar_url || undefined} icon={lead.telegram_avatar_url ? <UserOutlined /> : undefined}>
                        {initials}
                    </Avatar>
                    <div className="lc-main">
                        <h3 className="lc-title">{lead.title || `Лид #${lead.id}`}</h3>
                        <div className="lc-meta">
                            <span className={`lc-pill is-status${statusClass}`} title="Текущий статус в CRM Wiseway">
                                {canChangeStatus && statusGroups.length ? (
                                    <Select
                                        size="small"
                                        variant="borderless"
                                        showSearch
                                        optionFilterProp="label"
                                        popupMatchSelectWidth={false}
                                        loading={statusSaving}
                                        disabled={statusSaving}
                                        value={lead.crm_status || undefined}
                                        placeholder="Статус не прочитан"
                                        labelRender={() => statusLabel}
                                        options={statusGroups.map((group) => ({
                                            label: group.group,
                                            options: group.reasons.map((reason) => ({ value: reason.code, label: reason.label })),
                                        }))}
                                        onChange={(value) => changeStatus(value)}
                                    />
                                ) : statusLabel}
                            </span>
                            {lead.temperature && <Tag color={temperatureColor(lead.temperature, 'default')} style={{ marginInlineEnd: 0 }}>{temperatureLabel(lead.temperature)}</Tag>}
                            <span className="lc-pill">{lead.geo || 'ГЕО не задано'}</span>
                            {lead.crm_ww_id && <span className="lc-pill lc-mono">CRM WW {lead.crm_ww_id}</span>}
                            {lead.crm_telegram_mismatch && (
                                <Tooltip title="Телеграм в карточке CRM Wiseway другой - поля из неё в эту карточку не применяются, пока расхождение не уберут">
                                    <span className="lc-pill is-status">Телеграм в CRM WW не совпадает</span>
                                </Tooltip>
                            )}
                            {lead.telegram_id_mismatch && (
                                <Tooltip title="TG ID в карточке не совпадает с собеседником в диалоге. Карточку не перезаписываем - проверьте вручную">
                                    <span className="lc-pill is-status">TG ID не совпадает с диалогом</span>
                                </Tooltip>
                            )}
                            {lead.telegram_id_lookup_problem && (
                                <Tooltip title="TG ID искали в Telegram по username из карточки и не нашли. Исправьте username или впишите TG ID вручную - он появится и с первым сообщением">
                                    <span className="lc-pill is-status">{lead.telegram_id_lookup_problem}</span>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <div className="lc-actions">
                        {onOpenChat && lead.chat_id && (
                            <button className="lc-btn lc-wide-only" type="button" onClick={onOpenChat}><MessageOutlined /> Чат</button>
                        )}
                        <Popover trigger="click" placement="bottomRight" content={viewOptions}>
                            <button className="lc-btn lc-viewmenu" type="button" aria-label="Настройки вида">Вид</button>
                        </Popover>
                    </div>
                </div>
                <div className="lc-sub">
                    <span>Ответственный <b>{responsible}</b></span>
                    {lead.visibility_profile_name && <span>Ведёт <b>{lead.assigned_to_name || 'никто из команды'}</b></span>}
                    <span className="lc-opt">Аккаунт <b>{account}</b></span>
                    {lead.crm_status_at && <span>В статусе с <b>{dayjs(lead.crm_status_at).format('DD.MM HH:mm')}</b></span>}
                </div>
                <div className="lc-ready" title="Поля, без которых лида нельзя перевести на статусы онбординга — по правилам CRM Wiseway">
                    <span className="lc-ready-label"><span className="lc-long">Готовность к онбордингу</span><span className="lc-short">Онбординг</span></span>
                    <div className="lc-bar"><i className={readyDone === ONBOARDING_TOTAL ? 'is-full' : ''} style={{ width: `${(readyDone / ONBOARDING_TOTAL) * 100}%` }} /></div>
                    <span className="lc-ready-count">{readyDone} из {ONBOARDING_TOTAL}</span>
                </div>
                {gate && (
                    <div className="lc-gate" role="alert">
                        <button className="lc-gate-close" type="button" aria-label="Скрыть" onClick={() => setGate(null)}>✕</button>
                        <b>{gate.title}</b>
                        {gate.items.length > 0 && (
                            <>
                                {' '}— не заполнено {gate.items.length}:{' '}
                                {gate.items.map((item, index) => (
                                    <span key={item.label}>
                                        {index > 0 && ', '}
                                        {item.key ? <a onClick={() => flash(String(item.key))}>{item.label}</a> : item.label}
                                    </span>
                                ))}
                            </>
                        )}
                        {gate.text && <div style={{ marginTop: 4 }}>{gate.text}</div>}
                    </div>
                )}
            </header>

            <div className="lc-toolbar">
                <label className="lc-seg"><input type="checkbox" checked={opts.empty} onChange={(event) => setOpt('empty', event.target.checked)} /> Пустые поля</label>
                <label className="lc-seg"><input type="checkbox" checked={opts.missing} onChange={(event) => setOpt('missing', event.target.checked)} /> Только чего не хватает</label>
                <label className="lc-seg"><input type="checkbox" checked={opts.legacy} onChange={(event) => setOpt('legacy', event.target.checked)} /> Устаревшие поля</label>
                <span className="lc-spacer" />
                <button className="lc-btn is-ghost" type="button" onClick={() => setAll(!allClosed)}>{allClosed ? 'Развернуть всё' : 'Свернуть всё'}</button>
            </div>

            <nav className="lc-jump" aria-label="Разделы карточки">
                {GROUPS.map((group) => (
                    <button type="button" key={group.id} onClick={() => jumpTo(group)}>
                        {group.title}
                        {group.fields.some((field) => isMissing(field)) && <span className="lc-dot" title="Есть пробелы для онбординга" />}
                    </button>
                ))}
            </nav>

            <div className="lc-groups">{GROUPS.map(renderGroup)}</div>

            <footer className="lc-foot">
                <span>Создал: <b>{lead.created_by_name || 'система'}</b> · {dayjs(lead.created_at).format('DD.MM.YYYY HH:mm')}</span>
                <span>Изменено {dayjs(lead.updated_at).format('DD.MM.YYYY HH:mm')}</span>
            </footer>
        </div>
    )
}
